import { usePost } from "@albertine/api-client";
import type {
    ChatMessageProps,
    ChatReaction,
} from "@albertine/design-system/components/chat";
import type { ChatReplyStartMessage } from "@albertine/types";
import type { EmojiReaction } from "@albertine/types/openapi/concierge-v1";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAlbertineTranslation } from "albertine-shared-web";
import { useRef, useState } from "react";
import { parentOriginFromLocation, postMessageToParentFrame } from "@/lib/parentFrame";
import {
    buildSendGeneralChatMessageRequest,
    buildSendRequestChatMessageRequest,
    type ChatScope,
} from "../-data/chatApi";
import {
    type ChatOutboxDraft,
    type ChatOutboxEntry,
    enqueueChatOutboxEntry,
    markChatOutboxEntryFailed,
    markChatOutboxEntrySent,
} from "../-data/chatOutbox";
import { discardChatOutboxFiles } from "../-data/chatOutboxFiles";
import type { ChatThreadQueryKey } from "../-data/useChatThread";
import { assertNever, getErrorMessage } from "../-utils/chatErrors";
import { replyImageUrl } from "../-utils/replyImageUrl";
import {
    type ChatThreadInfiniteData,
    type ChatThreadPage,
    flattenChatThreadPages,
    getThreadMessageReaction,
    setThreadMessageReaction,
    updateChatThreadInfiniteData,
} from "../-utils/threadCache";
import { useChatAttachmentUploads } from "./useChatAttachmentUploads";

type SendChatMessageVariables = {
    draft: ChatOutboxDraft;
};

export function useChatMutations({
    queryKey,
    scope,
}: {
    queryKey: ChatThreadQueryKey;
    scope: ChatScope;
}) {
    const queryClient = useQueryClient();
    const t = useAlbertineTranslation();

    const [sendError, setSendError] = useState<string | null>(null);
    const [sendScrollGeneration, setSendScrollGeneration] = useState(0);
    const reactionSnapshots = useRef(new Map<string, EmojiReaction | null>());

    const { mutateAsync: sendGeneralChatMessage } = usePost("sendGeneralChatMessage");
    const { mutateAsync: sendRequestChatMessage } = usePost("sendRequestChatMessage");
    const { resolveDraftUploads } = useChatAttachmentUploads();

    function updateThread(
        update: (page: ChatThreadPage | undefined) => ChatThreadPage,
    ) {
        queryClient.setQueryData<ChatThreadInfiniteData>(queryKey, (data) =>
            updateChatThreadInfiniteData(data, update),
        );
    }

    const sendMutation = useMutation<null, unknown, SendChatMessageVariables>({
        mutationFn: async ({ draft }) => {
            const sendable = await resolveDraftUploads(draft);

            switch (scope.type) {
                case "general":
                    return sendGeneralChatMessage({
                        json: buildSendGeneralChatMessageRequest(sendable),
                    });
                case "request":
                    return sendRequestChatMessage({
                        param: { requestId: scope.requestId },
                        json: buildSendRequestChatMessageRequest(sendable),
                    });
                default:
                    return assertNever(scope);
            }
        },
        onMutate: ({ draft }) => {
            setSendError(null);
            const latestMessage = flattenChatThreadPages(
                queryClient.getQueryData<ChatThreadInfiniteData>(queryKey),
            )?.messages[0];
            enqueueChatOutboxEntry(draft, {
                afterSentAt: latestMessage?.sentAt,
            });
        },
        onSuccess: (_data, { draft }) => {
            markChatOutboxEntrySent(draft.sendingId);
            discardChatOutboxFiles(draft.uploads.map((upload) => upload.localId));
            void queryClient.invalidateQueries({ queryKey });
        },
        onError: (error, { draft }) => {
            markChatOutboxEntryFailed(draft.sendingId);
            setSendError(getErrorMessage(error));
        },
    });

    const reactMutation = usePost("memberReactToMessage", {
        onMutate: ({ param, json }) =>
            applyOptimisticReaction(param.messageId, json.reaction),
        onError: (error, { param }) => rollbackReaction(param.messageId, error),
        onSettled: (_data, _error, { param }) => settleReaction(param.messageId),
    });

    const removeReactionMutation = usePost("memberDeleteMessageReaction", {
        onMutate: ({ param }) => applyOptimisticReaction(param.messageId, null),
        onError: (error, { param }) => rollbackReaction(param.messageId, error),
        onSettled: (_data, _error, { param }) => settleReaction(param.messageId),
    });

    async function applyOptimisticReaction(
        messageId: string,
        reaction: EmojiReaction | null,
    ) {
        await queryClient.cancelQueries({ queryKey });

        reactionSnapshots.current.set(
            messageId,
            getThreadMessageReaction(
                flattenChatThreadPages(
                    queryClient.getQueryData<ChatThreadInfiniteData>(queryKey),
                ),
                messageId,
            ),
        );

        updateThread((page) => setThreadMessageReaction(page, messageId, reaction));
    }

    function rollbackReaction(messageId: string, error: unknown) {
        const snapshots = reactionSnapshots.current;

        if (snapshots.has(messageId)) {
            const previous = snapshots.get(messageId) ?? null;
            updateThread((page) => setThreadMessageReaction(page, messageId, previous));
        }

        setSendError(getErrorMessage(error));
    }

    function settleReaction(messageId: string) {
        reactionSnapshots.current.delete(messageId);
        void queryClient.invalidateQueries({ queryKey });
    }

    function sendDraft(draft: ChatOutboxDraft) {
        sendMutation.mutate({ draft });
        setSendScrollGeneration((generation) => generation + 1);
    }

    function retrySend(entry: ChatOutboxEntry) {
        sendDraft({
            sendingId: entry.sendingId,
            scopeKey: entry.scopeKey,
            message: entry.message,
            messageHtml: entry.messageHtml,
            attachments: entry.attachments,
            uploads: entry.uploads,
            confirmedBookings: entry.confirmedBookings,
            reply: entry.reply,
        });
    }

    function startReply(message: ChatMessageProps, messageText: string) {
        const replyMessage: ChatReplyStartMessage = {
            type: "chat.reply.start",
            reply: {
                id: message.id,
                title: t("chat.reply_title"),
                message: messageText,
                imageUrl: replyImageUrl(message),
            },
        };
        postMessageToParentFrame(parentOriginFromLocation(), replyMessage);
    }

    function react(messageId: string, reaction: ChatReaction) {
        reactMutation.mutate({
            param: { messageId },
            json: { reaction },
        });
    }

    function removeReaction(messageId: string) {
        removeReactionMutation.mutate({
            param: { messageId },
        });
    }

    return {
        sendError,
        sendScrollGeneration,
        updateThread,
        sendDraft,
        retrySend,
        startReply,
        react,
        removeReaction,
    };
}
